import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AuditOutlined, DownloadOutlined, FileAddOutlined, GiftOutlined, ProfileOutlined, SolutionOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../layouts/MainLayout';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { NoPermission } from '../../../components/NoPermission';
import Loading from '../../../components/Loading';
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { useCheckPenaltyDspImportGray } from '../../../graphql/queries/checkPenaltyDspImportGray';

const PartnerControlLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // 罚单管理中心灰度：与罚单导入共用 partner_penalty.dsp_import_whitelist（key 在后端固定），后端从登录态取 dspId。
    // 只在账号本就有该菜单资源码时才查：管控方账号不是 DSP，查了也必然隐藏，白发一次请求还会在后端刷 warn 日志
    const canSeePenaltyCenter = hasResource([ResourceCode.menu.PARTNER_PENALTY_CENTER]);
    const { isInGrayConfig: isPenaltyCenterGray, loading: grayLoading } = useCheckPenaltyDspImportGray({
        byLoginDsp: canSeePenaltyCenter
    });

    const menuItems = [
        {
            key: 'penaltyReview',
            label: t('partnerControl.menu.penaltyReview'),
            icon: <AuditOutlined />,
            display: hasResource([ResourceCode.menu.PARTNER_PENALTY_REVIEW]),
            children: [
                {
                    key: 'penaltyReview/generation',
                    label: t('partnerControl.menu.penaltyGeneration'),
                    icon: <FileAddOutlined />,
                    display: hasResource([ResourceCode.menu.PARTNER_PENALTY_GENERATION]),
                    onClick: () => navigate('/partner-control/penaltyReview/generation')
                },
                {
                    key: 'penaltyReview/appeal-review',
                    label: t('partnerControl.menu.appealReview'),
                    icon: <ProfileOutlined />,
                    display: hasResource([ResourceCode.menu.PARTNER_PENALTY_APPEAL_REVIEW]),
                    onClick: () => navigate('/partner-control/penaltyReview/appeal-review')
                }
            ]
        },
        {
            // DSP 侧自助查看/申诉自己的罚单，与管控方的「罚单审核」分组平级
            key: 'penaltyCenter',
            label: t('partnerControl.menu.penaltyCenter'),
            icon: <SolutionOutlined />,
            display: canSeePenaltyCenter && isPenaltyCenterGray,
            onClick: () => navigate('/partner-control/penaltyCenter')
        },
        {
            // 司机活动（原一级菜单「司机运营」下，2026-11 挪到合作商管控）；资源码与后端鉴权不变
            key: 'driver-activity',
            label: t('driverOps.menu.activity'),
            icon: <GiftOutlined />,
            display: hasResource([ResourceCode.menu.DRIVER_ACTIVITY]),
            onClick: () => navigate('/partner-control/driver-activity')
        },
        {
            key: 'download',
            label: t('partnerControl.menu.download'),
            icon: <DownloadOutlined />,
            display: hasResource([ResourceCode.menu.PARTNER_CONTROL_DOWNLOAD]),
            onClick: () => navigate('/partner-control/download')
        }
    ];

    // 灰度未返回前不跳转：此时 penaltyCenter 尚算不可见，只有该菜单权限的 DSP 账号会找不到可跳项；
    // 而本 effect 只在 pathname 变化时触发，灰度回来时地址没变就不会补跳，人会卡在根路径。
    // 把 grayLoading 纳入依赖，等结果到位后再跑一次
    useEffect(() => {
        if (grayLoading) return;
        const isRoot = location.pathname === '/partner-control' || location.pathname === '/partner-control/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, grayLoading]);

    // 二级菜单一个可点击项都没有（资源码不够）时，视为无权限，兜底展示 NoPermission 而不是空白页
    const hasVisibleMenu = useMemo(
        () => findFirstAvailableMenuItem(menuItems) !== null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuItems]
    );

    return (
        <MainLayout activeFirstMenu="partner-control" secondMenu={menuItems}>
            <div className="p-6 h-full flex flex-col">
                {/* 灰度未返回前不下结论，否则只有罚单中心权限的 DSP 账号会先被误判成无权限 */}
                {grayLoading ? <Loading /> : (hasVisibleMenu ? <Outlet /> : <NoPermission />)}
            </div>
        </MainLayout>
    );
};

export default PartnerControlLayout;
